<template>
    <!-- /developers: guía pública para integrar alojamientos con House & Flats
         (carga masiva por API, SQL, planilla u otros formatos). Contenido del
         documento "Carga Masiva de Alojamientos — House & Flats". -->
    <div class="dev">
        <header class="dev-hero">
            <p class="dev-hero__eyebrow">House &amp; Flats · Developers</p>
            <h1 class="dev-hero__title">Carga masiva de alojamientos</h1>
            <p class="dev-hero__lead">
                Cómo integrar tu sistema, PMS, API o base de datos con House &amp; Flats para publicar
                grandes volúmenes de propiedades de forma rápida, consistente y automatizada.
            </p>
            <div class="dev-hero__cta">
                <a :href="PLANTILLA" class="dev-btn dev-btn--primary" target="_blank" rel="noopener noreferrer">
                    <i class="fas fa-file-excel" aria-hidden="true" /> Plantilla oficial
                </a>
                <a href="#modalidades" class="dev-btn dev-btn--ghost" @click.prevent="ir('modalidades')">Ver modalidades</a>
            </div>
        </header>

        <div class="dev-layout">
            <!-- Índice: fijo al costado en escritorio, desplegable en celular -->
            <nav class="dev-toc" aria-label="Índice de la guía">
                <button type="button" class="dev-toc__toggle" :aria-expanded="tocAbierto ? 'true' : 'false'" aria-controls="dev-toc-list" @click="tocAbierto = !tocAbierto">
                    <i class="fas fa-list" aria-hidden="true" /> Contenido
                    <i :class="['fas', tocAbierto ? 'fa-chevron-up' : 'fa-chevron-down']" aria-hidden="true" />
                </button>
                <ol id="dev-toc-list" :class="['dev-toc__list', { 'is-open': tocAbierto }]">
                    <li v-for="s in SECCIONES" :key="s.id">
                        <a :href="`#${s.id}`" :class="{ 'is-active': activa === s.id }" :aria-current="activa === s.id ? 'true' : null" @click.prevent="ir(s.id)">{{ s.titulo }}</a>
                    </li>
                </ol>
            </nav>

            <main class="dev-content">
                <!-- ── Introducción ── -->
                <section id="introduccion" class="dev-sec">
                    <h2>Introducción</h2>
                    <p>
                        House &amp; Flats permite realizar la carga masiva de alojamientos a partir de diferentes fuentes de
                        información, facilitando la incorporación de grandes volúmenes de propiedades de forma rápida,
                        consistente y automatizada.
                    </p>
                    <p>Este proceso reduce los errores habituales de la carga manual:</p>
                    <ul class="dev-list">
                        <li>Duplicación de alojamientos.</li>
                        <li>Información incompleta o inconsistente.</li>
                        <li>Errores de tipeo.</li>
                        <li>Falta de características, tarifas o servicios.</li>
                        <li>Demoras en la publicación de múltiples propiedades.</li>
                    </ul>
                    <p>
                        La información puede incluir datos comerciales, ubicación, características, amenidades, reglas de
                        convivencia, descripción del alojamiento, disponibilidad e imágenes.
                    </p>
                </section>

                <!-- ── Modalidades ── -->
                <section id="modalidades" class="dev-sec">
                    <h2>Modalidades de integración</h2>
                    <p>
                        House &amp; Flats está diseñado para integrarse con diferentes sistemas, PMS, APIs, bases de datos y
                        fuentes de información utilizadas por nuestros partners.
                    </p>
                    <div class="dev-callout dev-callout--ok">
                        <i class="fas fa-check-circle" aria-hidden="true" />
                        <p>
                            <strong>No tenés que adaptar tu sistema.</strong> La estructura de origen no tiene que coincidir con la
                            de House &amp; Flats: nuestro equipo analiza la información disponible, hace el mapeo de campos,
                            transforma los datos y los adapta a la plataforma.
                        </p>
                    </div>
                    <div class="dev-cards">
                        <a v-for="m in MODALIDADES" :key="m.id" :href="`#${m.id}`" class="dev-card" @click.prevent="ir(m.id)">
                            <i :class="m.icono" aria-hidden="true" />
                            <span class="dev-card__title">{{ m.titulo }}</span>
                            <span class="dev-card__desc">{{ m.desc }}</span>
                        </a>
                    </div>
                </section>

                <!-- ── 1. API ── -->
                <section id="api" class="dev-sec">
                    <h2><span class="dev-num">1</span> Integración mediante API</h2>
                    <p>
                        Si tenés una API, House &amp; Flats se conecta directamente con tu sistema para obtener la información
                        de los alojamientos y, según el alcance acordado, también disponibilidad, tarifas, imágenes u otros datos.
                    </p>

                    <h3>¿Qué tenés que proporcionar?</h3>
                    <ul class="dev-checklist">
                        <li>URL base de la API.</li>
                        <li>Endpoints disponibles para consultar la información.</li>
                        <li>Documentación técnica de la API.</li>
                        <li>Método de autenticación utilizado.</li>
                        <li>Token, API Key o credenciales técnicas necesarias.</li>
                        <li>Estructura y ejemplos de las respuestas de cada endpoint.</li>
                        <li>Parámetros, filtros o paginación, cuando corresponda.</li>
                        <li>Información sobre los datos disponibles.</li>
                        <li>Cualquier consideración técnica necesaria para hacer las consultas.</li>
                    </ul>
                    <div class="dev-callout dev-callout--warn">
                        <i class="fas fa-lock" aria-hidden="true" />
                        <p>Compartí las credenciales por un canal privado acordado con el equipo, nunca en documentos públicos ni por chats abiertos.</p>
                    </div>

                    <h3>Ejemplo de mapeo</h3>
                    <p>Tu sistema puede usar nombres de campos completamente distintos. Nosotros hacemos la correspondencia:</p>
                    <div class="dev-table-wrap">
                        <table class="dev-table">
                            <caption class="sr-only">Correspondencia entre campos del proveedor y de House &amp; Flats</caption>
                            <thead><tr><th scope="col">Sistema del proveedor</th><th scope="col">House &amp; Flats</th></tr></thead>
                            <tbody>
                                <tr v-for="r in MAPEO" :key="r[0]"><td><code>{{ r[0] }}</code></td><td><code>{{ r[1] }}</code></td></tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>Estructura de referencia</h3>
                    <div class="dev-callout dev-callout--info">
                        <i class="fas fa-info-circle" aria-hidden="true" />
                        <p>
                            <strong>Esto NO es un requisito.</strong> Es un ejemplo de qué información puede recibir House &amp; Flats
                            y cómo se representa un alojamiento en nuestra plataforma. Tu API puede usar otros nombres, estructuras y formatos.
                        </p>
                    </div>
                    <dev-code :code="EJEMPLO_JSON" lang="json" titulo="Alojamiento en formato House & Flats" />

                    <p>Por ejemplo, tu API podría devolver:</p>
                    <dev-code :code="EJEMPLO_PROVEEDOR" lang="json" titulo="Respuesta de la API del proveedor" />
                    <p>Y House &amp; Flats lo transforma internamente:</p>
                    <dev-code :code="EJEMPLO_TRANSFORMACION" lang="text" titulo="Mapeo" />
                    <p>Así la integración se adapta a tu API existente, siempre que la información necesaria esté disponible.</p>
                </section>

                <!-- ── 2. SQL ── -->
                <section id="sql" class="dev-sec">
                    <h2><span class="dev-num">2</span> Integración mediante SQL / Base de datos</h2>
                    <p>Si no tenés API pero sí una base de datos con la información de tus alojamientos, la integración se puede hacer por SQL. Podés proporcionar:</p>
                    <ul class="dev-list">
                        <li>Un dump de la base de datos.</li>
                        <li>La estructura de las tablas relevantes.</li>
                        <li>Una exportación SQL.</li>
                        <li>Una copia de la información necesaria.</li>
                        <li>O el mecanismo de acceso acordado para la integración.</li>
                    </ul>
                    <p>
                        Analizamos la estructura existente y hacemos el mapeo, la transformación y la normalización.
                        No necesitás modificar tu base de datos. Por ejemplo, si tu sistema usa:
                    </p>
                    <dev-code :code="EJEMPLO_TABLA" lang="text" titulo="Tabla de origen" />
                    <p>House &amp; Flats asocia esos campos con los correspondientes de su estructura interna.</p>
                    <h3>Ideal para</h3>
                    <ul class="dev-tags">
                        <li>Migraciones iniciales</li>
                        <li>Grandes volúmenes de alojamientos</li>
                        <li>Proveedores con base de datos propia</li>
                        <li>Sistemas sin API</li>
                    </ul>
                </section>

                <!-- ── 3. Excel ── -->
                <section id="excel" class="dev-sec">
                    <h2><span class="dev-num">3</span> Carga mediante Excel / Google Sheets</h2>
                    <p>
                        Si no tenés API ni acceso a base de datos, podés entregar la información en una planilla estructurada
                        usando nuestra plantilla oficial de carga masiva. En esta modalidad la plantilla <strong>es</strong> el
                        formato de intercambio, así que es importante respetar su estructura.
                    </p>
                    <a :href="PLANTILLA" class="dev-template" target="_blank" rel="noopener noreferrer">
                        <i class="fas fa-file-excel" aria-hidden="true" />
                        <span>
                            <strong>Plantilla de Carga Masiva — House &amp; Flats</strong>
                            <small>Google Sheets · se abre en una pestaña nueva</small>
                        </span>
                        <i class="fas fa-external-link-alt" aria-hidden="true" />
                    </a>
                    <p>Se entrega en <code>.xlsx</code>, o compartiendo el Google Sheets con permisos de lectura. Las reglas para completarla están en <a href="#plantilla" @click.prevent="ir('plantilla')">Plantilla de carga</a>.</p>
                </section>

                <!-- ── 4. Otros ── -->
                <section id="otros" class="dev-sec">
                    <h2><span class="dev-num">4</span> Otros formatos o sistemas</h2>
                    <p>Si usás otro sistema o formato, lo evaluamos para definir la mejor alternativa. Por ejemplo:</p>
                    <ul class="dev-tags">
                        <li>CSV personalizado</li>
                        <li>XML</li>
                        <li>Exportaciones de PMS</li>
                        <li>Feeds de información</li>
                        <li>Servicios externos</li>
                        <li>Otros sistemas de gestión</li>
                    </ul>
                    <p>Analizamos la información disponible y definimos con vos el mecanismo más adecuado.</p>
                </section>

                <!-- ── Plantilla ── -->
                <section id="plantilla" class="dev-sec">
                    <h2>Plantilla de carga — Excel / Google Sheets</h2>
                    <p>La plantilla contiene los campos requeridos, con el formato y los valores que espera la plataforma.</p>
                    <h3>Recomendaciones de uso</h3>
                    <ol class="dev-steps-list">
                        <li>Hacé una copia de la plantilla (<kbd>Archivo</kbd> → <kbd>Hacer una copia</kbd>) antes de completarla.</li>
                        <li><strong>No cambies los nombres de las columnas</strong>: se usan para el mapeo automático.</li>
                        <li>Completá <strong>una fila por alojamiento</strong>.</li>
                        <li>Usá <code>idcodigo</code> como identificador único.</li>
                        <li>No elimines columnas. Si un campo no aplica, dejalo vacío.</li>
                        <li>Respetá los formatos indicados. Valores numéricos sin símbolos de moneda (<code>700</code>, no <code>US$ 700</code>).</li>
                        <li>En características y amenidades: <code>1</code> = sí, <code>0</code> = no.</li>
                        <li>Para varias imágenes, usá las URLs de ese alojamiento.</li>
                        <li>Entregala en <code>.xlsx</code> o CSV, o compartí el enlace con permisos de lectura.</li>
                    </ol>
                    <div class="dev-callout dev-callout--info">
                        <i class="fas fa-info-circle" aria-hidden="true" />
                        <p>La plantilla no es obligatoria si tenés API, base de datos u otra fuente compatible: en ese caso mapeamos y normalizamos los datos de tu sistema.</p>
                    </div>
                </section>

                <!-- ── Datos ── -->
                <section id="datos" class="dev-sec">
                    <h2>Datos necesarios para la integración</h2>
                    <p>
                        Varían según el tipo de alojamiento y el alcance de cada integración, pero como mínimo tiene que haber
                        información suficiente para identificar, procesar y publicar el alojamiento.
                    </p>
                    <h3>Datos principales para crear la integración por API</h3>
                    <div class="dev-table-wrap">
                        <table class="dev-table dev-table--fields">
                            <caption class="sr-only">Campos principales de un alojamiento</caption>
                            <thead><tr><th scope="col">Campo</th><th scope="col">Descripción</th><th scope="col">Valores / ejemplo</th></tr></thead>
                            <tbody>
                                <tr v-for="c in CAMPOS" :key="c.campo">
                                    <td><code>{{ c.campo }}</code></td>
                                    <td>{{ c.desc }}</td>
                                    <td><code v-if="c.ej">{{ c.ej }}</code></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="dev-muted">La disponibilidad de cada campo depende de tu sistema. Analizamos cada integración para definir cómo obtener y transformar la información.</p>
                </section>

                <!-- ── iCal ── -->
                <section id="ical" class="dev-sec">
                    <h2>Disponibilidad mediante iCal</h2>
                    <p>El campo <code>icalUrl</code> asocia un calendario iCal externo para sincronizar la disponibilidad del alojamiento con otras plataformas. El enlace debe:</p>
                    <ul class="dev-checklist">
                        <li>Ser válido y accesible.</li>
                        <li>Corresponder al alojamiento indicado.</li>
                        <li>Mantenerse activo para permitir la sincronización.</li>
                    </ul>
                    <p>Si el alojamiento no usa un calendario externo, el campo se evalúa según las necesidades de la integración.</p>
                    <div class="dev-callout dev-callout--info">
                        <i class="fas fa-random" aria-hidden="true" />
                        <p>
                            <strong>Se pueden combinar fuentes.</strong> El iCal no tiene que venir de la misma fuente que el resto:
                            por ejemplo, alojamientos por API, imágenes por Google Drive y disponibilidad por iCal.
                        </p>
                    </div>
                </section>

                <!-- ── Imágenes ── -->
                <section id="imagenes" class="dev-sec">
                    <h2>Imágenes</h2>
                    <p>Se envían como URLs públicas en el campo <code>imageUrls</code>:</p>
                    <dev-code :code="EJEMPLO_IMAGENES" lang="json" titulo="imageUrls" />
                    <p>Las URLs tienen que ser <strong>públicamente accesibles</strong> para que podamos procesarlas.</p>

                    <h3>Alternativa: Google Drive</h3>
                    <p>Si las imágenes no están en URLs públicas, se pueden entregar en un Google Drive organizado. Para evitar errores de asociación:</p>
                    <ul class="dev-checklist">
                        <li>Una carpeta identificable por alojamiento.</li>
                        <li>Carpetas organizadas de forma consistente.</li>
                        <li>El nombre de la carpeta identifica al alojamiento por su <code>idcodigo</code> u otro identificador único.</li>
                        <li>Todas las imágenes de un alojamiento dentro de su carpeta.</li>
                        <li>Permisos de acceso suficientes para procesar los archivos.</li>
                    </ul>
                    <dev-code :code="EJEMPLO_DRIVE" lang="text" titulo="Estructura sugerida" />
                </section>

                <!-- ── Requisitos mínimos ── -->
                <section id="requisitos" class="dev-sec">
                    <h2>Requisitos mínimos</h2>
                    <p>El formato de origen puede variar, pero cada alojamiento tiene que contar con:</p>
                    <ul class="dev-checklist dev-checklist--cols">
                        <li>Identificador único del alojamiento.</li>
                        <li>Tipo y categoría.</li>
                        <li>Modalidad de arriendo.</li>
                        <li>Moneda y precio.</li>
                        <li>Dirección y/o ubicación.</li>
                        <li>Capacidad máxima, cuando corresponda.</li>
                        <li>Características y amenidades según el tipo.</li>
                        <li>Información descriptiva y reglas, cuando corresponda.</li>
                        <li>Imágenes (URLs públicas, Google Drive u otro mecanismo acordado).</li>
                        <li>Calendario iCal, cuando corresponda.</li>
                    </ul>
                    <p class="dev-muted">El formato de origen no cambia los requisitos mínimos. Si los nombres, estructuras o formatos son distintos, los mapeamos, transformamos y normalizamos antes de la importación definitiva.</p>
                </section>

                <!-- ── Proceso ── -->
                <section id="proceso" class="dev-sec">
                    <h2>Proceso de integración</h2>
                    <ol class="dev-timeline">
                        <li v-for="(p, i) in PROCESO" :key="p.titulo">
                            <span class="dev-timeline__n" aria-hidden="true">{{ i + 1 }}</span>
                            <div>
                                <h3>{{ p.titulo }}</h3>
                                <p>{{ p.desc }}</p>
                                <dev-code v-if="p.code" :code="p.code" lang="text" />
                            </div>
                        </li>
                    </ol>
                </section>
            </main>
        </div>
    </div>
</template>

<script>
import DevCode from '@/components/Developers/DevCode.vue';

const PLANTILLA = 'https://docs.google.com/spreadsheets/d/1mkNRat5_brUa9YdiAfGx2FmknSCm460kboSD5ITMbtA/edit?usp=sharing';

const SECCIONES = [
    { id: 'introduccion', titulo: 'Introducción' },
    { id: 'modalidades', titulo: 'Modalidades de integración' },
    { id: 'api', titulo: '1. API' },
    { id: 'sql', titulo: '2. SQL / Base de datos' },
    { id: 'excel', titulo: '3. Excel / Google Sheets' },
    { id: 'otros', titulo: '4. Otros formatos' },
    { id: 'plantilla', titulo: 'Plantilla de carga' },
    { id: 'datos', titulo: 'Datos necesarios' },
    { id: 'ical', titulo: 'Disponibilidad (iCal)' },
    { id: 'imagenes', titulo: 'Imágenes' },
    { id: 'requisitos', titulo: 'Requisitos mínimos' },
    { id: 'proceso', titulo: 'Proceso de integración' },
];

const MODALIDADES = [
    { id: 'api', icono: 'fas fa-plug', titulo: 'API', desc: 'Nos conectamos directo a tu sistema o PMS.' },
    { id: 'sql', icono: 'fas fa-database', titulo: 'SQL / Base de datos', desc: 'Dump, exportación o acceso acordado.' },
    { id: 'excel', icono: 'fas fa-table', titulo: 'Excel / Google Sheets', desc: 'Con nuestra plantilla oficial.' },
    { id: 'otros', icono: 'fas fa-puzzle-piece', titulo: 'Otros formatos', desc: 'CSV, XML, feeds, exportaciones de PMS…' },
];

const MAPEO = [
    ['property_id', 'idcodigo'],
    ['property_type', 'tipo / categoria'],
    ['monthly_price', 'arriendo_total'],
    ['max_guests', 'maximo_huespedes'],
    ['photos', 'imageUrls'],
    ['calendar', 'icalUrl'],
];

const CAMPOS = [
    { campo: 'idcodigo', desc: 'Identificador único del alojamiento en tu sistema. Evita duplicados.', ej: 'TEST-001' },
    { campo: 'tipo', desc: 'Alojamiento entero o habitación privada.', ej: 'entero | privado' },
    { campo: 'categoria', desc: 'Tipo de propiedad.', ej: 'casa | departamento | residencia' },
    { campo: 'direccion', desc: 'Dirección completa.', ej: 'Av Santa Fe 1234, Palermo, Buenos Aires' },
    { campo: 'latitud / longitud', desc: 'Coordenadas del alojamiento.', ej: '-34.5895 / -58.3974' },
    { campo: 'arriendo_total', desc: 'Precio mensual del alojamiento, sin símbolo de moneda.', ej: '700' },
    { campo: 'arriendo_comunes', desc: 'Gastos comunes incluidos.', ej: '100' },
    { campo: 'arriendo_limpieza', desc: 'Costo de limpieza.', ej: '30' },
    { campo: 'arriendo_basicos', desc: 'Servicios básicos (luz, agua, gas…).', ej: '50' },
    { campo: 'sobre_nosotros', desc: 'Presentación del proveedor.', ej: '' },
    { campo: 'reglas', desc: 'Reglas de convivencia.', ej: '' },
    { campo: 'espacios_comunes', desc: 'Espacios compartidos a los que acceden los huéspedes.', ej: '' },
    { campo: 'uso_cocina', desc: 'Condiciones de uso de la cocina.', ej: '' },
    { campo: 'aseo_limpieza', desc: 'Frecuencia y alcance de la limpieza.', ej: '' },
    { campo: 'amenidades', desc: 'Una columna por amenidad (tv, sofa, cama, balcon, wifi…).', ej: '1 = sí · 0 = no' },
    { campo: 'imageUrls', desc: 'Lista de URLs públicas de las imágenes.', ej: '["https://…/1.jpg"]' },
    { campo: 'icalUrl', desc: 'Calendario iCal con la disponibilidad.', ej: 'https://…/basic.ics' },
];

const EJEMPLO_JSON = `{
  "accommodation": {
    "idcodigo": "TEST-001",
    "numero": "101",
    "nombre": "Departamento amoblado en Palermo",
    "tipo": "entero",
    "categoria": "departamento",
    "modalidad_tiempo": "mensual",
    "moneda": "USD",
    "arriendo_total": 700,
    "arriendo_basicos": 50,
    "arriendo_comunes": 100,
    "arriendo_limpieza": 30,
    "arriendo_diario": 45,
    "direccion": "Av Santa Fe 1234, Palermo, Buenos Aires, Argentina",
    "latitud": -34.5895,
    "longitud": -58.3974,
    "maximo_huespedes": 2,
    "m2": 42,
    "wifi": 1,
    "cocina": 1,
    "lavadora": 1,
    "sobre_nosotros": "Somos una empresa especializada en alojamiento temporal para estudiantes internacionales y profesionales.",
    "reglas": "No se permiten fiestas ni eventos. Prohibido fumar dentro de la propiedad.",
    "espacios_comunes": "Los huéspedes tienen acceso a sala de estar compartida, comedor, terraza y área de coworking.",
    "uso_cocina": "La cocina compartida puede utilizarse libremente e incluye utensilios básicos.",
    "aseo_limpieza": "La limpieza de las áreas comunes se realiza dos veces por semana."
  },
  "icalUrl": "https://calendar.google.com/calendar/ical/example/basic.ics",
  "imageUrls": [
    "https://example.com/images/depto-1.jpg",
    "https://example.com/images/depto-2.jpg"
  ]
}`;

const EJEMPLO_PROVEEDOR = `{
  "property_id": "ABC-123",
  "property_type": "apartment",
  "monthly_price": 700,
  "max_guests": 2
}`;

const EJEMPLO_TRANSFORMACION = `property_id     →  idcodigo
property_type   →  categoria
monthly_price   →  arriendo_total
max_guests      →  maximo_huespedes`;

const EJEMPLO_TABLA = `properties
├── property_id
├── property_name
├── property_type
├── monthly_price
├── capacity
└── address`;

const EJEMPLO_IMAGENES = `{
  "imageUrls": [
    "https://example.com/images/depto-1.jpg",
    "https://example.com/images/depto-2.jpg"
  ]
}`;

const EJEMPLO_DRIVE = `Google Drive
│
├── TEST-001 - Departamento Palermo
│   ├── foto-01.jpg
│   ├── foto-02.jpg
│   └── foto-03.jpg
│
├── TEST-002 - Departamento Recoleta
│   ├── foto-01.jpg
│   ├── foto-02.jpg
│   └── foto-03.jpg
│
└── TEST-003 - Habitación Belgrano
    ├── foto-01.jpg
    └── foto-02.jpg`;

const PROCESO = [
    { titulo: 'Recepción de datos', desc: 'Recibimos la información en el formato disponible.', code: 'API / PMS  →  SQL / Base de datos  →  Excel / CSV  →  Otro formato' },
    { titulo: 'Análisis de la fuente', desc: 'Analizamos la estructura del sistema de origen y qué información está disponible y cómo usarla.' },
    { titulo: 'Validación', desc: 'Verificamos que estén los datos mínimos requeridos y que los valores sean válidos.' },
    { titulo: 'Mapeo y normalización', desc: 'Transformamos los campos del proveedor a la estructura interna. Lo mismo aplica a tarifas, características, ubicación, imágenes y disponibilidad.', code: 'Sistema del proveedor  →  property_id  →  Mapeo House & Flats  →  idcodigo' },
    { titulo: 'Control de duplicados', desc: 'Usamos identificadores únicos (idcodigo) para no crear alojamientos duplicados.' },
    { titulo: 'Importación', desc: 'Incorporamos los alojamientos con sus características, tarifas, imágenes y disponibilidad.' },
    { titulo: 'Publicación y sincronización', desc: 'Los alojamientos quedan disponibles para su gestión y publicación en House & Flats.' },
];

export default {
    name: 'Developers',
    components: { DevCode },
    data() {
        return {
            PLANTILLA, SECCIONES, MODALIDADES, MAPEO, CAMPOS, PROCESO,
            EJEMPLO_JSON, EJEMPLO_PROVEEDOR, EJEMPLO_TRANSFORMACION, EJEMPLO_TABLA, EJEMPLO_IMAGENES, EJEMPLO_DRIVE,
            activa: SECCIONES[0].id,
            tocAbierto: false,
        };
    },
    mounted() {
        this.prevTitle = document.title;
        document.title = 'Developers · Carga masiva | House & Flats';
        // Resalta en el índice la sección que se está leyendo.
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
                const visibles = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visibles.length) this.activa = visibles[0].target.id;
            }, { rootMargin: '-90px 0px -60% 0px' });
            this.SECCIONES.forEach((s) => { const el = document.getElementById(s.id); if (el) this.observer.observe(el); });
        }
        // Link directo a una sección (/developers#imagenes).
        if (this.$route.hash) this.$nextTick(() => this.ir(this.$route.hash.slice(1), false));
    },
    beforeDestroy() {
        if (this.observer) this.observer.disconnect();
        document.title = this.prevTitle;
    },
    methods: {
        ir(id, actualizarUrl = true) {
            const el = document.getElementById(id);
            if (!el) return;
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 90, behavior: reduce ? 'auto' : 'smooth' });
            this.activa = id;
            this.tocAbierto = false;
            // replaceState y no $router.replace: el scrollBehavior del router
            // saltaría al #hash sin el margen del header y cortaría el scroll suave.
            if (actualizarUrl && window.location.hash !== `#${id}`) window.history.replaceState(window.history.state, '', `#${id}`);
            // Foco al título para lectores de pantalla y teclado.
            const h = el.querySelector('h2');
            if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
        },
    },
};
</script>

<style scoped lang="scss">
$naranja-500: #ff7a00;
$naranja-600: #e8640a;
$naranja-700: #c24f05;
$verde: $naranja-700; // títulos y acentos: el naranja oscuro del formulario (contraste AA sobre blanco)
$naranja: $naranja-500;
$texto: #1d1a17;
$suave: #6b625b;
$borde: #efe6dd;

.dev { color: $texto; padding-bottom: 64px; }

/* ── Hero ── */
.dev-hero {
    max-width: 1180px;
    margin: 0 auto;
    padding: 56px 24px 40px;
    &__eyebrow { margin: 0 0 10px; color: $naranja; font-weight: 700; font-size: .85rem; letter-spacing: .08em; text-transform: uppercase; }
    &__title { margin: 0 0 14px; color: $verde; font-weight: 800; font-size: clamp(2rem, 4vw, 2.8rem); line-height: 1.1; }
    &__lead { margin: 0; max-width: 720px; color: $suave; font-size: 1.1rem; line-height: 1.6; }
    &__cta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 24px; }
}
.dev-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 20px; border-radius: 10px;
    font-weight: 700; font-size: .95rem; text-decoration: none !important;
    border: 1.5px solid transparent; transition: background .15s, border-color .15s;
    &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
    &--primary { background: linear-gradient(135deg, $naranja-500, $naranja-600); color: #fff !important; box-shadow: 0 10px 24px -10px rgba(232, 100, 10, .75); &:hover { box-shadow: 0 14px 30px -10px rgba(232, 100, 10, .85); } }
    &--ghost { background: #fff; color: $verde !important; border-color: $borde; &:hover { border-color: $verde; } }
}

/* ── Layout: índice + contenido ── */
.dev-layout {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 24px;
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 48px;
    align-items: start;
}
.dev-toc {
    position: sticky; top: 96px;
    &__toggle { display: none; }
    &__list {
        list-style: none; margin: 0; padding: 0 0 0 2px;
        border-left: 2px solid $borde;
        a {
            display: block; margin-left: -2px; padding: 7px 14px;
            border-left: 2px solid transparent;
            color: $suave; font-size: .9rem; text-decoration: none;
            &:hover { color: $verde; }
            &:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
            &.is-active { color: $verde; font-weight: 700; border-left-color: $naranja; }
        }
    }
}

/* ── Contenido ── */
.dev-content { min-width: 0; }
.dev-sec {
    padding: 8px 0 40px;
    margin-bottom: 8px;
    border-bottom: 1px solid $borde;
    scroll-margin-top: 90px;
    &:last-child { border-bottom: 0; }
    h2 {
        display: flex; align-items: center; gap: 12px;
        margin: 24px 0 14px; color: $verde; font-weight: 800; font-size: 1.6rem; line-height: 1.25;
        &:focus { outline: none; }
    }
    h3 { margin: 26px 0 10px; color: $verde; font-weight: 700; font-size: 1.12rem; }
    p { line-height: 1.7; margin: 0 0 12px; }
    a:not(.dev-btn):not(.dev-card):not(.dev-template) { color: $naranja-700; font-weight: 600; }
    code { padding: 2px 6px; border-radius: 5px; background: #fff7ef; color: #9a3f04; font: 500 .86em 'Roboto Mono', monospace; }
    kbd { padding: 1px 6px; border: 1px solid $borde; border-bottom-width: 2px; border-radius: 5px; background: #fff; font-size: .85em; }
}
.dev-num {
    display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 34px; height: 34px; border-radius: 10px;
    background: #ffecd9; color: $naranja-700; font-size: 1rem;
}
.dev-muted { color: $suave; font-size: .94rem; }

/* Listas */
.dev-list { padding-left: 22px; margin: 0 0 14px; li { margin: 5px 0; line-height: 1.6; } }
.dev-checklist {
    list-style: none; padding: 0; margin: 0 0 16px;
    li { position: relative; padding: 6px 0 6px 30px; line-height: 1.55; }
    li::before {
        content: '\f00c'; font-family: 'Font Awesome 5 Free'; font-weight: 900;
        position: absolute; left: 0; top: 7px;
        width: 20px; height: 20px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        background: #ffecd9; color: $naranja-700; font-size: .65rem;
    }
    &--cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 24px; }
}
.dev-steps-list {
    padding-left: 0; list-style: none; counter-reset: paso; margin: 0 0 16px;
    li { counter-increment: paso; position: relative; padding: 8px 0 8px 40px; line-height: 1.6; }
    li::before {
        content: counter(paso); position: absolute; left: 0; top: 7px;
        width: 26px; height: 26px; border-radius: 8px;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, $naranja-500, $naranja-600); color: #fff; font-size: .8rem; font-weight: 700;
    }
}
.dev-tags {
    list-style: none; padding: 0; margin: 0 0 16px; display: flex; flex-wrap: wrap; gap: 8px;
    li { padding: 6px 12px; border-radius: 999px; background: #fff7ef; border: 1px solid $borde; color: $verde; font-size: .88rem; font-weight: 600; }
}

/* Avisos */
.dev-callout {
    display: flex; gap: 12px; align-items: flex-start;
    margin: 16px 0; padding: 14px 16px; border-radius: 12px; border: 1px solid;
    > i { margin-top: 4px; font-size: 1.05rem; }
    p { margin: 0; }
    &--ok   { background: #fff7ef; border-color: #ffd6ad; > i { color: $naranja-600; } }
    &--info { background: #EFF6FF; border-color: #BFDBFE; > i { color: #1D4ED8; } }
    &--warn { background: #FFFBEB; border-color: #FDE68A; > i { color: #B45309; } }
}

/* Tarjetas de modalidades */
.dev-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-top: 18px; }
.dev-card {
    display: flex; flex-direction: column; gap: 6px;
    padding: 18px; border: 1px solid $borde; border-radius: 14px; background: #fff;
    text-decoration: none !important; color: $texto;
    transition: border-color .15s, box-shadow .15s, transform .15s;
    > i { color: $naranja; font-size: 1.3rem; margin-bottom: 4px; }
    &__title { color: $verde; font-weight: 700; }
    &__desc { color: $suave; font-size: .88rem; line-height: 1.45; }
    &:hover { border-color: $naranja-500; box-shadow: 0 6px 20px rgba(232, 100, 10, .12); transform: translateY(-2px); }
    &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
}

/* Link a la plantilla */
.dev-template {
    display: flex; align-items: center; gap: 14px;
    margin: 16px 0; padding: 16px 18px;
    border: 1.5px solid #ffd6ad; border-radius: 14px; background: #fff7ef;
    text-decoration: none !important; color: $texto;
    > i:first-child { font-size: 1.8rem; color: $naranja-600; }
    > i:last-child { margin-left: auto; color: $suave; }
    span { display: flex; flex-direction: column; }
    strong { color: $verde; }
    small { color: $suave; }
    &:hover { border-color: $naranja-500; }
    &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
}

/* Tablas */
.dev-table-wrap { overflow-x: auto; margin: 12px 0 18px; border: 1px solid $borde; border-radius: 12px; }
.dev-table {
    width: 100%; border-collapse: collapse; font-size: .92rem; background: #fff;
    th { background: #fff7ef; color: $verde; text-align: left; font-weight: 700; padding: 11px 14px; border-bottom: 1px solid $borde; }
    td { padding: 10px 14px; border-bottom: 1px solid $borde; vertical-align: top; line-height: 1.5; }
    tr:last-child td { border-bottom: 0; }
    &--fields td:first-child { white-space: nowrap; }
}

/* Proceso */
.dev-timeline {
    list-style: none; padding: 0; margin: 8px 0 0;
    li { position: relative; display: flex; gap: 16px; padding-bottom: 8px; }
    li:not(:last-child)::after { content: ''; position: absolute; left: 17px; top: 40px; bottom: 0; width: 2px; background: $borde; }
    h3 { margin: 6px 0 6px; }
    > li > div { min-width: 0; flex: 1; }
    &__n {
        flex-shrink: 0; width: 36px; height: 36px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, $naranja-500, $naranja-600); color: #fff; font-weight: 700;
    }
}

.sr-only {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

/* ── Celular / tablet ── */
@media (max-width: 900px) {
    .dev-hero { padding: 36px 18px 24px; }
    .dev-layout { grid-template-columns: 1fr; gap: 0; padding: 0 18px; }
    .dev-toc {
        top: 64px; z-index: 5; margin: 0 -18px 16px; padding: 8px 18px;
        background: rgba(255, 255, 255, .96); border-bottom: 1px solid $borde;
        &__toggle {
            display: flex; align-items: center; gap: 8px; width: 100%;
            padding: 10px 0; border: 0; background: none; color: $verde; font-weight: 700;
            i:last-child { margin-left: auto; }
            &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
        }
        &__list { display: none; border-left: 0; padding-bottom: 8px; &.is-open { display: block; } a { padding-left: 4px; } }
    }
    .dev-checklist--cols { grid-template-columns: 1fr; }
    .dev-sec h2 { font-size: 1.35rem; }
}
@media (prefers-reduced-motion: reduce) {
    .dev-card, .dev-btn { transition: none; }
    .dev-card:hover { transform: none; }
}
</style>
